import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { Badge } from "@/components/ui/badge/Badge";
import type { Alert } from "@/services/api/guardiansv2/types";
import { alertBadgeColor, alertBadgeLabel } from "@/services/api/guardiansv2";
import { AuditScopeLinks } from "../shared/AuditScopeLinks";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";

export interface AlertCardProps {
  alert: Alert;
  scopeLabels: ScopeLabelCache;
  selected: boolean;
  onSelect: () => void;
  onArchive: () => void;
}

export function AlertCard({
  alert,
  scopeLabels,
  selected,
  onSelect,
  onArchive,
}: AlertCardProps) {
  const badgeColor = alertBadgeColor(alert);
  const ringClass =
    badgeColor === "amber" ? "ring-1 ring-amber-500/25" : "ring-1 ring-red-500/20";
  const iconClass =
    badgeColor === "amber"
      ? "bg-amber-500/15 text-amber-300"
      : "bg-red-500/15 text-red-300";

  return (
    <MissionControlCard
      data-testid={`alert-card-${alert._id}`}
      compact
      selected={selected}
      onClick={onSelect}
      className={ringClass}
      icon={
        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full ${iconClass}`}
        >
          <FontAwesomeIcon icon={faBell} className="text-xs" />
        </span>
      }
      title={
        <span className="text-sm font-medium text-slate-100 line-clamp-2">
          {alert.title}
        </span>
      }
      titleBadges={
        <Badge color={badgeColor} className="text-[10px] uppercase">
          {alertBadgeLabel(alert)}
        </Badge>
      }
      timestamp={
        <span className="text-xs text-slate-400 tabular-nums">
          {formatRelativeTime(alert.created_at)}
        </span>
      }
      subtitle={
        <span className="mt-1.5 flex flex-col gap-1.5">
          {alert.summary ? (
            <span className="text-xs text-slate-400 line-clamp-2">{alert.summary}</span>
          ) : null}
          <AuditScopeLinks scope={alert.scope} labels={scopeLabels} compact />
        </span>
      }
      actions={
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onArchive();
          }}
          className="rounded-lg bg-slate-800 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-700"
          data-testid={`alert-archive-${alert._id}`}
        >
          Hide
        </button>
      }
    />
  );
}
